import { createAsyncThunk } from '@reduxjs/toolkit';

import instance, { API_BASE_PATH } from 'configs/requests';

const GATEWAY_URL = '/clinician-gateway';
const BILLING_URL = `${GATEWAY_URL}/billing`;

/**
 * A document's `url` is a path relative to the API itself (`/billing/documents/<type>/<code>`), so
 * it is what the axios instance would need — not what a browser can follow. Opening one in a new
 * tab is plain navigation, so it needs the same prefixes the instance adds: the `/api` base and the
 * gateway segment.
 */
export const billingDocumentHref = (url: string) => `/api${GATEWAY_URL}${url}`;

/**
 * Opens a PDF the API serves behind the auth guard.
 *
 * `billingDocumentHref` works as a plain `<a href>` because the masters are served
 * unauthenticated on purpose: a practice reads Terms before it has an account. Everything else
 * under `/billing` needs the bearer token, which a browser navigation does not carry, so it has to
 * be fetched through the instance and handed to the viewer as a blob. The invoice PDF link had the
 * same shape and would have answered 401 the first time an organisation had an invoice to open.
 *
 * The tab is opened synchronously, before the await: a popup opened after one is blocked.
 */
export const openBillingPdf = async (path: string): Promise<void> => {
  /*
   * Opened before the await, because a popup is only allowed while the click is still being
   * handled and these documents are fetched with the session's bearer token.
   *
   * **Without `noopener`**, deliberately. `window.open` returns null by specification when
   * `noopener` is given, so the handle was always null, every call fell to the branch below, and
   * that branch navigated the tab to a `blob:` URL - which Chrome refuses at top level. Both paths
   * were dead and the button did nothing at all (Charles, 2026-09-14). The tab we hand the blob to
   * is our own object URL, so there is no opener to withhold.
   */
  const viewer = window.open('', '_blank');
  try {
    const { data } = await instance.get<Blob>(path, { responseType: 'blob' });
    const blobUrl = URL.createObjectURL(data);
    if (viewer) {
      viewer.location.href = blobUrl;
    } else {
      // Popup genuinely blocked. An anchor click is a user-initiated navigation, which is allowed
      // where assigning window.location is not.
      const link = document.createElement('a');
      link.href = blobUrl;
      link.target = '_blank';
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      link.remove();
    }
    // Long enough for the viewer to have fetched it, after which the object is just a leak.
    window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
  } catch (error) {
    viewer?.close();
    throw error;
  }
};

/** The branded statement for one invoice. Gid-bound like every other billing read. */
export const billingInvoicePdfPath = (gid: string, invoiceId: string) =>
  `${BILLING_URL}/invoices/${invoiceId}/pdf?gid=${encodeURIComponent(gid)}`;

/**
 * The organisation's executed copy of an agreement [D64]: the master carries CareSimple's
 * signature and this carries the practice's, because the sealed master may not be re-rendered.
 */
/**
 * The organisation's Agreement Certificate: evidence of acceptance plus the accepted text of every
 * document, as one file. One per organisation, not one per document -- the agreement is the set,
 * and a provider sending it to an auditor should not have to know they needed two files.
 */
export const billingAgreementCertificatePath = (gid: string) =>
  `${BILLING_URL}/agreements/certificate?gid=${encodeURIComponent(gid)}`;

/**
 * A standing document (Terms, BAA), built here rather than taken from the listing response.
 *
 * The listing returns `url: "/billing/documents/baa/082626"` - rooted at the API, with no gateway
 * segment - and passing that straight to the axios instance produced
 * `/api/billing/documents/baa/082626`. There is no such route, so nginx's SPA fallback answered
 * 200 with index.html and pdfjs was handed a web page to parse: "Failed to load CS-BAA 082626"
 * (dev01, 2026-09-15). Every other billing path in this file is built from BILLING_URL; this one
 * had no reason to be the exception.
 */
export const billingStandingDocumentPath = (documentType: string, dateCode: string) =>
  `${BILLING_URL}/documents/${documentType}/${dateCode}`;

/**
 * The same document, as a URL the **browser** can follow.
 *
 * axios prepends its baseURL; an `<a href>` does not, so the bare path fell through to the SPA
 * router and downloaded `index.html` instead of the PDF -- a provider could not retain either
 * agreement during signup (Charles, 2026-09-16). Built from the same two pieces as the fetch path
 * so they cannot drift apart again.
 */
export const billingStandingDocumentHref = (documentType: string, dateCode: string) =>
  `${API_BASE_PATH}${billingStandingDocumentPath(documentType, dateCode)}`;

export const fetchBillingBalance = createAsyncThunk('billing/fetchBalance', async (gid: string) => {
  const { data } = await instance.get<IBillingBalance>(`${BILLING_URL}/balance`, {
    params: { gid }
  });
  return data;
});

export const fetchBillingBatches = createAsyncThunk(
  'billing/fetchBatches',
  async ({ gid, includeExpired = true }: { gid: string; includeExpired?: boolean }) => {
    const { data } = await instance.get<{ items: IBillingBatch[] }>(`${BILLING_URL}/batches`, {
      params: { gid, includeExpired }
    });
    return data.items;
  }
);

export const fetchBillingLedger = createAsyncThunk(
  'billing/fetchLedger',
  async ({ gid, limit = 50, before }: { gid: string; limit?: number; before?: string | null }) => {
    const { data } = await instance.get<IBillingLedgerResponse>(`${BILLING_URL}/ledger`, {
      params: { gid, limit, before: before || undefined }
    });
    return { ...data, append: !!before };
  }
);

export const fetchBillingInvoices = createAsyncThunk(
  'billing/fetchInvoices',
  async (gid: string) => {
    const { data } = await instance.get<{ items: IBillingInvoiceSummary[] }>(
      `${BILLING_URL}/invoices`,
      { params: { gid } }
    );
    return data.items;
  }
);

export const fetchBillingPaymentMethods = createAsyncThunk(
  'billing/fetchPaymentMethods',
  async (gid: string) => {
    const { data } = await instance.get<{ items: IBillingPaymentMethod[] }>(
      `${BILLING_URL}/payment-methods`,
      { params: { gid } }
    );
    return data.items;
  }
);

export const fetchBillingRateCard = createAsyncThunk(
  'billing/fetchRateCard',
  async (gid: string) => {
    const { data } = await instance.get<IBillingRateCard>(
      `/clinician-gateway/groups/${gid}/rate-card`
    );
    return data;
  }
);

/** Plain API calls used by modals (no store state needed). */
export const getBillingInvoiceDetail = async (gid: string, invoiceId: string) => {
  const { data } = await instance.get<IBillingInvoiceDetail>(
    `${BILLING_URL}/invoices/${invoiceId}`,
    { params: { gid } }
  );
  return data;
};

export const postBillingTopupIntent = async (payload: IBillingTopupIntentRequest) => {
  const { data } = await instance.post<IBillingTopupIntent>(`${BILLING_URL}/topup/intent`, payload);
  return data;
};

export const postBillingPaymentMethodSetupIntent = async (gid: string) => {
  const { data } = await instance.post<{ clientSecret: string }>(
    `${BILLING_URL}/payment-methods/setup-intent`,
    { gid }
  );
  return data;
};

export const postBillingPaymentMethod = async (payload: {
  gid: string;
  stripePaymentMethodId: string;
  makeDefault: boolean;
}) => {
  const { data } = await instance.post<IBillingPaymentMethod>(
    `${BILLING_URL}/payment-methods`,
    payload
  );
  return data;
};

export const putBillingDefaultPaymentMethod = async (gid: string, paymentMethodId: string) => {
  const { data } = await instance.put<IBillingPaymentMethod>(
    `${BILLING_URL}/payment-methods/${paymentMethodId}/default`,
    { gid }
  );
  return data;
};

export const deleteBillingPaymentMethod = async (gid: string, paymentMethodId: string) => {
  await instance.delete(`${BILLING_URL}/payment-methods/${paymentMethodId}`, {
    params: { gid }
  });
};

/**
 * Billing access (role 12). The write calls are allowed for a billing admin *or* a group admin,
 * so they are reachable both from Billing & Credits -> Settings and from Admin -> Users.
 */
export const getBillingAdmins = async (gid: string) => {
  const { data } = await instance.get<{ items: IBillingAdmin[] }>(`${BILLING_URL}/admins`, {
    params: { gid }
  });
  return data.items;
};

export const postBillingAdmin = async (gid: string, aid: string) => {
  const { data } = await instance.post<IBillingAdmin>(`${BILLING_URL}/admins`, { gid, aid });
  return data;
};

/** Rejects with a 409 when `aid` is the organization's last billing admin, or its account owner. */
export const deleteBillingAdmin = async (gid: string, aid: string) => {
  await instance.delete(`${BILLING_URL}/admins/${aid}`, { params: { gid } });
};

/**
 * Assigns account ownership to `aid`. Two callers qualify: the current owner, transferring it on
 * from Billing & Credits -> Settings, and a SYSOP, assigning it from Admin -> Users whoever the
 * owner is — the recovery path for an organization that has none. The API answers 403 for anyone
 * else, and 409 when `aid` already owns the account. Owner implies billing (Alex, 2026-09-02): the
 * assignment grants billing access to `aid` when they do not hold it yet, so `aid` need only be a
 * member of the organization — refetch groupAdmins afterwards to pick the new grant up.
 */
export const putBillingAdminOwner = async (gid: string, aid: string) => {
  const { data } = await instance.put<IBillingAdmin>(`${BILLING_URL}/admins/${aid}/owner`, { gid });
  return data;
};

/**
 * Sets `aid`'s invoice-email preference. Every billing admin receives the monthly invoice unless
 * they opt out, so `invoiceEmail: false` is the deliberate choice. A user may always set their own;
 * setting another account's needs the access-granting capability (billing or group admin).
 */
export const putBillingAdminNotifications = async (
  gid: string,
  aid: string,
  invoiceEmail: boolean
) => {
  const { data } = await instance.put<IBillingAdmin>(`${BILLING_URL}/admins/${aid}/notifications`, {
    gid,
    invoiceEmail
  });
  return data;
};

/**
 * Agreements. The signup click-through is recorded against a verified email, an IP and a user
 * agent — no account exists yet — so the portal asks the logged-in admin to re-affirm once, which
 * ties the acceptance to a real account. `VIEW_BILLING` on the gid: only call it for a prepaid
 * organization whose current user holds billing access.
 */
/** The organisation's billing state, plus what it already told us, for forms to start from. */
export const getBillingStatus = async (gid: string) => {
  const { data } = await instance.get<IBillingStatus>(`${BILLING_URL}/status`, { params: { gid } });
  return data;
};

export const getBillingAgreementsStatus = async (gid: string) => {
  const { data } = await instance.get<IBillingAgreementStatus>(`${BILLING_URL}/agreements/status`, {
    params: { gid }
  });
  return data;
};

/**
 * Records the one-time re-affirmation of Terms and BAA for the signed-in account and returns the
 * refreshed status. Idempotent per account per version; the server picks the versions currently in
 * force, so the client never names an identifier.
 */
export const postBillingAgreementsReaffirm = async (gid: string) => {
  const { data } = await instance.post<IBillingAgreementStatus>(
    `${BILLING_URL}/agreements/reaffirm`,
    { gid }
  );
  return data;
};

/**
 * Metadata for the standing documents (terms, baa, pricing). Unauthenticated and not gid-bound —
 * the same call the signup wizard makes. `url` is present only once a master is registered, so
 * expect none until the pre-signed documents are published.
 */
export const getBillingDocuments = async () => {
  const { data } = await instance.get<{ items: IBillingDocument[] }>(`${BILLING_URL}/documents`);
  return data.items;
};

/**
 * An operator refund [D59]. SYSOP only, on the organisation route rather than the gid-bound
 * billing routes — a refund is CareSimple acting on the practice's balance, not the practice
 * acting on its own. Nothing is refunded at Stripe by this call; the operator does that
 * separately and passes the refund id for reconciliation.
 */
export const postBillingRefund = async (
  gid: string,
  payload: {
    batchId: string;
    amountMinor: number;
    reasonCode: BillingRefundReason;
    note?: string;
    stripeRefundId?: string;
    clientRequestId: string;
  }
) => {
  const { data } = await instance.post<IBillingRefund>(
    `${GATEWAY_URL}/billing/organizations/${encodeURIComponent(gid)}/refunds`,
    payload
  );
  return data;
};

/** Discounts and organisation prices. SYSOP only. */
export const postBillingEntitlement = async (gid: string, input: IBillingEntitlementInput) => {
  const { data } = await instance.post<IBillingEntitlement>(`${BILLING_URL}/entitlements`, {
    gid,
    ...input
  });
  return data;
};

/** Kind, SKU, value and valid-from are accepted only while nothing is consumed or reserved. */
export const patchBillingEntitlement = async (
  entitlementId: string,
  patch: IBillingEntitlementPatch
) => {
  const { data } = await instance.patch<IBillingEntitlement>(
    `${BILLING_URL}/entitlements/${encodeURIComponent(entitlementId)}`,
    patch
  );
  return data;
};

export const postBillingEntitlementVoid = async (entitlementId: string, reason: string) => {
  const { data } = await instance.post<IBillingEntitlement>(
    `${BILLING_URL}/entitlements/${encodeURIComponent(entitlementId)}/void`,
    { reason }
  );
  return data;
};

export const getBillingEntitlementPreview = async (
  target: BillingPreviewTarget,
  input: Omit<IBillingEntitlementInput, 'label' | 'reason'>,
  replacesEntitlementId?: string
) => {
  const { data } = await instance.get<{ items: IBillingEntitlementPreviewItem[] }>(
    `${BILLING_URL}/entitlements/preview`,
    {
      params: {
        ...target,
        kind: input.kind,
        sku: input.sku || undefined,
        value: input.value,
        quantityCap: input.quantityCap ?? undefined,
        validFrom: input.validFrom || undefined,
        validTo: input.validTo || undefined,
        replacesEntitlementId
      }
    }
  );
  return data.items;
};

export const getBillingPublishedRateCards = async () => {
  const { data } = await instance.get<IBillingPublishedRateCard[]>(
    `${BILLING_URL}/rate-cards/published`
  );
  return data;
};

/** Saves the organisation's overrides as a new version; an empty set returns it to the base card. */
export const putBillingOrgRateCard = async (gid: string, payload: IBillingOrgRateCardPayload) => {
  const { data } = await instance.put<IBillingRateCard>(
    `${BILLING_URL}/organizations/${encodeURIComponent(gid)}/rate-card`,
    payload
  );
  return data;
};
